import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { adminOrdersService, type Order as BackendOrder, type OrdersPage } from '../../services/adminOrders';
import ErrorModal from '../../components/ErrorModal';
import DateRangePicker from '../../components/DateRangePicker';

function formatCurrency(value: number) {
  return value.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
}

function statusBadgeClassesBackend(status: string) {
  switch (status) {
    case 'paid':
    case 'delivered':
    case 'completed':
      return 'badge-modern bg-green-100 border-green-200 text-green-700';
    case 'pending':
    case 'created':
    case 'preparing':
    case 'ready':
    case 'confirmed':
      return 'badge-modern bg-yellow-100 border-yellow-200 text-yellow-700';
    case 'cancelled':
      return 'badge-modern bg-red-100 border-red-200 text-red-700';
    default:
      return 'badge-modern bg-gray-100 border-gray-200 text-gray-700';
  }
}

function statusLabel(status: string) {
  switch (status) {
    case 'created':
      return 'Creada';
    case 'pending':
      return 'Pendiente';
    case 'confirmed':
      return 'Confirmada';
    case 'preparing':
      return 'Preparando';
    case 'ready':
      return 'Lista';
    case 'delivered':
      return 'Entregada';
    case 'completed':
      return 'Completada';
    case 'paid':
      return 'Pagada';
    case 'cancelled':
      return 'Cancelada';
    default:
      return status;
  }
}
// (Componente DateRangePicker moderno importado)

export default function AdminSales() {
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();

  const [currentPage, setCurrentPage] = useState(1);
  const todayStr = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);
  const [fromDate, setFromDate] = useState<string>(todayStr);
  const [toDate, setToDate] = useState<string>(todayStr);
  const pageSize = 10;

  const [orders, setOrders] = useState<BackendOrder[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('created'); // 'todas' para no filtrar
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!pickerRef.current) return;
      const target = e.target as Node;
      if (!pickerRef.current.contains(target)) {
        setShowPicker(false);
      }
    };
    if (showPicker) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showPicker]);

  const load = useMemo(() => {
    return async () => {
      if (!user?.uid || !selectedRestaurantId) return;
      setLoading(true);
      setError(null);
      try {
        const res: OrdersPage = await adminOrdersService.listPaged(user.uid, selectedRestaurantId, {
          page: currentPage,
          size: pageSize,
          status: statusFilter === 'todas' ? undefined : statusFilter,
          from: fromDate || undefined,
          to: (toDate || fromDate) || undefined,
        });
        setOrders(res.data);
        setTotal(res.pagination.total);
        setPages(res.pagination.pages);
      } catch (e: any) {
        setError(e?.message || 'Error cargando órdenes');
      } finally {
        setLoading(false);
      }
    };
  }, [user?.uid, selectedRestaurantId, currentPage, pageSize, fromDate, toDate, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const pageStart = total ? (currentPage - 1) * pageSize + 1 : 0;

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString('es-MX')} ${d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const renderPageNumbers = () => {
    const maxNumbers = 5;
    const start = Math.max(1, currentPage - Math.floor(maxNumbers / 2));
    const end = Math.min(pages, start + maxNumbers - 1);
    const numbers = [];
    for (let p = start; p <= end; p++) numbers.push(p);
    return numbers.map(p => (
      <button
        key={p}
        onClick={() => setCurrentPage(p)}
        className={`px-3 py-1.5 rounded-md text-sm border ${
          p === currentPage ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
        }`}
      >
        {p}
      </button>
    ));
  };

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 space-y-4">
      {/* Filtros */}
      <div className="card p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="relative" ref={pickerRef}>
            <button
              type="button"
              onClick={() => setShowPicker(v => !v)}
              className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50 flex items-center gap-2"
              title="Seleccionar fechas"
            >
              📅
              <span className="text-gray-700">
                {fromDate}{fromDate !== toDate ? ` → ${toDate}` : ''}
              </span>
            </button>
            {showPicker && (
              <div className="absolute z-50 mt-2">
                <DateRangePicker
                  from={fromDate}
                  to={toDate}
                  onChange={(f, t) => {
                    setFromDate(f);
                    setToDate(t);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="statusFilter" className="text-sm text-gray-600">Estatus</label>
            <select
              id="statusFilter"
              className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="todas">Todas</option>
              <option value="created">Creada</option>
              <option value="pending">Pendiente</option>
              <option value="confirmed">Confirmada</option>
              <option value="preparing">Preparando</option>
              <option value="ready">Lista</option>
              <option value="delivered">Entregada</option>
              <option value="completed">Completada</option>
              <option value="paid">Pagada</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </div>
          <div className="text-sm text-gray-500">
            Mostrando {pageStart}-{Math.min(pageStart + pageSize - 1, total)} de {total} órdenes
          </div>
        </div>
      </div>

      {/* Tabla de órdenes */}
      <div className="card p-3 sm:p-4">
        {loading && <div className="text-center py-6 text-gray-600">Cargando...</div>}
        {error && <div className="text-center py-6 text-red-600">{error}</div>}
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Orden</th>
                <th>Fecha</th>
                <th>Mesa</th>
                <th>Mesero</th>
                <th>Método</th>
                <th>Estatus</th>
                <th className="text-right">Items</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => {
                const total = order.totalAmount || 0;
                const itemsCount = order.items?.reduce((s, it) => s + (Number(it.quantity) || 0), 0) || 0;
                return (
                  <tr key={order.id}>
                    <td className="whitespace-nowrap">{order.id}</td>
                    <td className="whitespace-nowrap">{formatDateTime(order.createdAt)}</td>
                    <td>{order.tableNumber}</td>
                    <td>{order.waiterName || '-'}</td>
                    <td>-</td>
                    <td>
                      <span className={statusBadgeClassesBackend(order.status)}>
                        {statusLabel(order.status)}
                      </span>
                    </td>
                    <td className="text-right">{itemsCount}</td>
                    <td className="text-right">{formatCurrency(total)}</td>
                  </tr>
                );
              })}
              {!loading && orders.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-sm text-gray-500 py-6">Sin órdenes para este estatus</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-md text-sm border bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <div className="flex items-center gap-2">
              {renderPageNumbers()}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(pages, p + 1))}
              className="px-3 py-1.5 rounded-md text-sm border bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              disabled={currentPage === pages}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
      <ErrorModal isOpen={!!error} title="Error" message={error || ''} onClose={() => setError(null)} />
    </div>
  );
}

